import { fmtDate, MAILRU_DOMAINS, mailService } from '@jt/core';
import { NOTIFY_EVENTS, type IntegrationsStatus, type Settings } from '@jt/shared';
import type { ReactNode } from 'react';
import { TextField, TimeField } from '../components/fields';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon } from '../components/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, del, post, put } from '../api/client';
import { useEffect, useState } from 'react';
import { integrationsKey, useIntegrations } from '../pages/integrations';
import {
  AnthropicKeyGuide,
  GoogleClientIdGuide,
  GoogleClientSecretGuide,
  TelegramAppGuide,
  TelegramTokenGuide,
} from './guides';
import { SecretField, SecretsReadOnlyNote } from './Secrets';
import { useIsAdmin } from '../api/hooks';
import { useLocation } from 'react-router-dom';
import { TalantoSettings } from './Talanto';

type SetFn = (f: Partial<Omit<Settings, 'version' | 'telegramLinked'>>) => Promise<unknown>;

const NOTIFY_LABELS: Record<(typeof NOTIFY_EVENTS)[number], string> = {
  interview: 'Приглашения на интервью',
  screening: 'Приглашения на скрининг',
  offer: 'Офферы',
  rejection: 'Отказы',
  message: 'Сообщения рекрутеров',
  applied: 'Подтверждения откликов',
  feed: 'Новые вакансии из ленты',
};

/** ok — работает (салатовый), bad — ошибка (красный), off — не настраивалось (серый). */
type Health = 'ok' | 'bad' | 'off';

function Row({ ok, name, children }: { ok: Health | boolean; name: string; children: ReactNode }) {
  const h: Health = ok === true ? 'ok' : ok === false ? 'off' : ok;
  return (
    <div className="lrow">
      <span className={`sdot ${h}`} style={{ marginTop: 4 }} aria-hidden="true" />
      <div className="lm">
        <b>{name}</b>
        <div className="meta">{children}</div>
      </div>
    </div>
  );
}

const when = (iso: string | null) => (iso ? `${fmtDate(iso.slice(0, 10))}, ${iso.slice(11, 16)} UTC` : '');

/**
 * Карточка подключения: заголовок — точка состояния и одна строка статуса, по нажатию раскрываются настройки.
 * Неработающее открыто сразу, работающее свёрнуто; ссылка #id раскрывает карточку.
 */
function ConnCard({
  id,
  name,
  health,
  status,
  children,
}: {
  id: string;
  name: string;
  /** undefined — состояние ещё грузится */
  health: Health | undefined;
  status: ReactNode;
  children: ReactNode;
}) {
  const loc = useLocation();
  const [open, setOpen] = useState<boolean | null>(null);
  useEffect(() => {
    if (loc.hash === `#${id}`) setOpen(true);
  }, [loc.hash, id]);
  const isOpen = open ?? (health !== undefined && health !== 'ok');
  return (
    <section className={`box conn-card${isOpen ? ' open' : ''}`} id={id}>
      <h2>
        <button
          type="button"
          className="conn-h"
          aria-expanded={isOpen}
          aria-controls={`${id}-body`}
          onClick={() => setOpen(!isOpen)}
        >
          <span className={`sdot ${health ?? 'off'}`} aria-hidden="true" />
          <span className="conn-t">
            {name}
            <span className="meta">{status}</span>
          </span>
          <span className="conn-chev" aria-hidden="true">
            <Icon name="chevD" size={20} />
          </span>
        </button>
      </h2>
      {isOpen && (
        <div className="conn-b" id={`${id}-body`}>
          {children}
        </div>
      )}
    </section>
  );
}

/** Вкладка «Подключения»: почта, Telegram, Talanto, Claude — каждое своей карточкой; ниже — фоновые задачи. */
export function ConnectionsTab({ s, set }: { s: Settings; set: SetFn }) {
  const admin = useIsAdmin();
  const d = useIntegrations().data;
  const n = (v: number) => v.toLocaleString('ru-RU');
  const g = d?.gmail;
  const t = d?.talanto;
  const c = d?.claude;
  return (
    <>
      <ConnCard
        id="gmail"
        name={g?.service === 'mailru' ? 'Почта Mail.ru' : 'Gmail'}
        health={d && mailHealth(g)}
        status={
          !g
            ? '…'
            : g.needsReauth
              ? 'доступ к Gmail истёк — подключите заново'
              : g.connected
                ? `${g.email ?? 'подключена'} · ${g.lastScanAt ? `проверено ${when(g.lastScanAt)}` : 'ещё не проверялась'}${g.lastError ? ` · ошибка: ${g.lastError}` : ''}`
                : 'не подключена — письма об откликах не попадают в трекер'
        }
      >
        <GmailSettings s={s} set={set} />
      </ConnCard>
      <ConnCard
        id="telegram"
        name="Telegram-бот"
        health={d && (d.telegram.linked ? 'ok' : 'off')}
        status={
          !d
            ? '…'
            : d.telegram.linked
              ? `привязан${d.telegram.botUsername ? ` · @${d.telegram.botUsername}` : ''} — уведомления, дайджест, пересылка вакансий`
              : d.telegram.configured
                ? 'бот не привязан — уведомлений нет'
                : admin
                  ? 'нет токена бота'
                  : 'бот ещё не настроен администратором'
        }
      >
        <TelegramSettings s={s} set={set} />
      </ConnCard>
      <ConnCard
        id="talanto"
        name="Talanto"
        health={t && (!t.connected ? 'off' : t.needsReauth || t.lastError ? 'bad' : 'ok')}
        status={
          !t
            ? '…'
            : t.connected
              ? `связано откликов: ${t.linked}${t.needsReauth ? ' · ключ не принят' : t.lastError ? ` · ошибка: ${t.lastError}` : ''}`
              : 'не подключён'
        }
      >
        <TalantoSettings />
      </ConnCard>
      <ConnCard
        id="claude"
        name="Claude"
        health={c && (c.configured ? 'ok' : 'off')}
        status={
          !c
            ? '…'
            : c.configured
              ? `${n(c.tokensThisMonth)} токенов за месяц${c.monthlyLimit ? ` из ${n(c.monthlyLimit)}` : ''}${c.monthlyLimit && c.tokensThisMonth >= c.monthlyLimit * 0.8 ? ' — израсходовано больше 80 % лимита' : ''}`
              : admin
                ? 'нет ключа — ИИ-кнопки неактивны'
                : 'администратор ещё не задал ключ — ИИ-кнопки неактивны'
        }
      >
        <ClaudeSettings s={s} set={set} />
      </ConnCard>
      {d && d.jobs.length > 0 && (
        <details className="raw box">
          <summary>
            Фоновые задачи · {d.jobs.some((j) => j.ok === false) ? 'есть ошибки' : 'работают'}
          </summary>
          <div className="list" style={{ marginTop: 8 }}>
            {d.jobs.map((j) => (
              <Row key={j.name} ok={j.ok === false ? 'bad' : j.lastFinishedAt ? 'ok' : 'off'} name={j.name}>
                {j.lastFinishedAt ? `последний запуск ${when(j.lastFinishedAt)}` : 'ещё не запускалась'}
                {j.message ? ` · ${j.message}` : ''}
              </Row>
            ))}
          </div>
        </details>
      )}
    </>
  );
}

function GmailSettings({ s, set }: { s: Settings; set: SetFn }) {
  const admin = useIsAdmin();
  const mailru = useIntegrations().data?.gmail.service === 'mailru';
  const num = (v: string | null, def: number) => (v ? Number(v) : def);
  return (
    <>
      <p className="muted sm" style={{ marginTop: 0 }}>
        Трекер читает письма об откликах (только чтение) и предлагает внести их в вакансии.
      </p>
      <MailAccount />
      {admin && <GoogleClientKeys />}
      <div className="grid" style={{ marginTop: 14 }}>
        <TextField
          label="Проверять каждые, минут (5–120)"
          name="gmailScanIntervalMin"
          type="number"
          value={s.gmailScanIntervalMin}
          onSave={(v) => set({ gmailScanIntervalMin: num(v, 10) })}
        />
        <TextField
          label="Глубина первой проверки, дней"
          name="gmailFirstScanDays"
          type="number"
          value={s.gmailFirstScanDays}
          onSave={(v) => set({ gmailFirstScanDays: num(v, 21) })}
        />
        {/* условие в синтаксисе поиска Gmail — у Mail.ru такого поиска нет */}
        {!mailru && (
          <TextField
            label="Дополнительное условие поиска"
            name="gmailExtraQuery"
            className="full"
            value={s.gmailExtraQuery}
            placeholder="from:talent.acme.com OR label:jobs"
            onSave={(v) => set({ gmailExtraQuery: v ?? '' })}
          />
        )}
        <TextField
          label={
            mailru
              ? 'Разобранные письма — в папку Mail.ru (пусто — не переносить)'
              : 'Разобранные письма — в ярлык Gmail (пусто — не переносить)'
          }
          name="gmailMoveLabel"
          className="full"
          value={s.gmailMoveLabel}
          placeholder="Работа"
          onSave={(v) => set({ gmailMoveLabel: (v ?? '').trim() })}
        />
        <p className="muted sm full" style={{ margin: '-4px 0 0' }}>
          {mailru
            ? 'Письма, попавшие в трекер (применённые и пропущенные события, подборки LinkedIn), переносятся из «Входящих» в эту папку. Ничего не удаляется.'
            : 'Письма, попавшие в трекер (применённые и пропущенные события, подборки LinkedIn), получают ярлык и уходят из «Входящих» — как «Архивировать». Ничего не удаляется. Работает с паролем приложения (IMAP).'}
        </p>
        <label
          className="f full"
          style={{ flexDirection: 'row', alignItems: 'center', gap: 10, color: 'var(--ink)' }}
        >
          <input
            type="checkbox"
            checked={s.gmailAutoApply}
            onChange={(e) => void set({ gmailAutoApply: e.target.checked })}
          />
          Подтверждения откликов применять сразу, без подтверждения
        </label>
      </div>
    </>
  );
}

/** Почта пользователя: подключена — адрес, проверка, отключение; нет — пароль приложения или вход через Google. */
function MailAccount() {
  const ui = useUI();
  const qc = useQueryClient();
  const q = useIntegrations();
  const g = q.data?.gmail;
  const [mode, setMode] = useState<'imap' | 'google'>('imap');
  const [service, setService] = useState<'gmail' | 'mailru'>('gmail');
  if (!g) return null;
  if (g.connected)
    return (
      <div className="mailacc">
        <p className="sm" style={{ margin: '0 0 8px' }}>
          Подключена почта <b>{g.email}</b> ·{' '}
          {g.service === 'mailru'
            ? 'Mail.ru, пароль для внешнего приложения'
            : g.provider === 'imap'
              ? 'пароль приложения'
              : 'вход через Google'}
          {g.needsReauth && (
            <span style={{ color: 'var(--danger)' }}> — доступ истёк, подключите заново</span>
          )}
        </p>
        <div className="btnrow">
          <ScanNow />
          <ConfirmButton
            className="btn sm ghost danger"
            confirmText="Отключить почту?"
            onConfirm={async () => {
              await del(g.provider === 'imap' ? '/integrations/imap' : '/integrations/google');
              await qc.invalidateQueries({ queryKey: integrationsKey });
              ui.toast('Почта отключена');
            }}
          >
            Отключить почту
          </ConfirmButton>
        </div>
        {g.needsReauth &&
          (g.provider === 'imap' ? (
            <ImapForm email={g.email ?? ''} service={g.service ?? 'gmail'} />
          ) : (
            <a className="btn primary" style={{ marginTop: 8 }} href="/api/integrations/google/connect">
              <Icon name="mail" /> Переподключить Gmail
            </a>
          ))}
      </div>
    );
  return (
    <>
      <div className="seg mailsw" role="radiogroup" aria-label="Какая почта" style={{ margin: '4px 0 12px' }}>
        <button
          type="button"
          role="radio"
          aria-checked={service === 'gmail'}
          onClick={() => setService('gmail')}
        >
          Gmail
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={service === 'mailru'}
          onClick={() => setService('mailru')}
        >
          Mail.ru
        </button>
      </div>
      {service === 'gmail' && g.googleAvailable && (
        <div
          className="seg mailsw"
          role="radiogroup"
          aria-label="Способ подключения почты"
          style={{ margin: '4px 0 12px' }}
        >
          <button type="button" role="radio" aria-checked={mode === 'imap'} onClick={() => setMode('imap')}>
            Пароль приложения — проще
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'google'}
            onClick={() => setMode('google')}
          >
            Вход через Google
          </button>
        </div>
      )}
      {service === 'mailru' ? (
        <ImapForm key="mailru" email="" service="mailru" />
      ) : mode === 'google' && g.googleAvailable ? (
        <a className="btn primary" href="/api/integrations/google/connect">
          <Icon name="mail" /> Войти через Google
        </a>
      ) : (
        <ImapForm key="gmail" email="" service="gmail" />
      )}
    </>
  );
}

/**
 * Почта + пароль приложения (Gmail) или пароль для внешнего приложения (Mail.ru): проверяется пробным
 * входом, хранится зашифрованным.
 */
function ImapForm({ email: initial, service }: { email: string; service: 'gmail' | 'mailru' }) {
  const mailru = service === 'mailru';
  const ui = useUI();
  const qc = useQueryClient();
  const [email, setEmail] = useState(initial);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <form
      className="imapf"
      onSubmit={async (e) => {
        e.preventDefault();
        // сервер выбирается по адресу: ящик не Mail.ru ушёл бы в Gmail
        if (mailru && mailService(email) !== 'mailru')
          return setErr(`Это не адрес Mail.ru — нужен ящик ${MAILRU_DOMAINS.map((d) => `@${d}`).join(', ')}`);
        setBusy(true);
        setErr('');
        try {
          qc.setQueryData(
            integrationsKey,
            await put<IntegrationsStatus>('/integrations/imap', { email, password }),
          );
          setPassword('');
          ui.toast('Почта подключена — первая проверка уже идёт');
        } catch (x) {
          setErr(
            x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не получилось. Повторите.',
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="grid">
        <label className="f">
          {mailru ? 'Почта Mail.ru' : 'Почта Gmail'}
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={mailru ? 'you@mail.ru' : 'you@gmail.com'}
          />
        </label>
        <label className="f">
          {mailru ? 'Пароль для внешнего приложения' : 'Пароль приложения Google'}
          <input
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mailru ? 'из настроек безопасности Mail.ru' : '16 букв, можно с пробелами'}
          />
        </label>
      </div>
      {mailru ? (
        <p className="muted sm" style={{ margin: '6px 0 8px' }}>
          Не пароль от почты: в Mail.ru откройте «Настройки» → «Безопасность» → «Пароли для внешних
          приложений», добавьте пароль с доступом к почте по IMAP и вставьте его сюда. Трекер читает письма и
          переносит разобранные в папку из настроек ниже — ничего не удаляет и не отправляет.
        </p>
      ) : (
        <p className="muted sm" style={{ margin: '6px 0 8px' }}>
          Не пароль от почты: откройте{' '}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
            myaccount.google.com/apppasswords
          </a>{' '}
          (нужна двухэтапная аутентификация), создайте пароль с любым названием и вставьте сюда. Доступ —
          только чтение писем.
        </p>
      )}
      {err && (
        <p className="sm" role="alert" style={{ color: 'var(--danger)', margin: '0 0 8px' }}>
          {err}
        </p>
      )}
      <button className="btn primary" type="submit" disabled={busy || !email || !password}>
        {busy ? 'Проверяю…' : 'Проверить и подключить'}
      </button>
    </form>
  );
}

/** Администратор: OAuth-клиент Google — тогда пользователи могут подключать почту входом через Google. */
function GoogleClientKeys() {
  return (
    <details className="raw" style={{ marginTop: 14 }}>
      <summary>Вход через Google для всех пользователей (администратор)</summary>
      <SecretsReadOnlyNote />
      <SecretField
        name="googleClientId"
        label="Google Client ID"
        placeholder="….apps.googleusercontent.com"
        info={<GoogleClientIdGuide />}
        help="Где взять — кнопка «i» рядом с названием поля."
      />
      <SecretField
        name="googleClientSecret"
        label="Google Client secret"
        placeholder="GOCSPX-…"
        info={<GoogleClientSecretGuide />}
      />
    </details>
  );
}

/** Дни недели для дайджеста: значение как Date.getDay() (0 — воскресенье), порядок — с понедельника. */
const DAYS: [number, string, string][] = [
  [1, 'Пн', 'Понедельник'],
  [2, 'Вт', 'Вторник'],
  [3, 'Ср', 'Среда'],
  [4, 'Чт', 'Четверг'],
  [5, 'Пт', 'Пятница'],
  [6, 'Сб', 'Суббота'],
  [0, 'Вс', 'Воскресенье'],
];

function TelegramSettings({ s, set }: { s: Settings; set: SetFn }) {
  const ui = useUI();
  const admin = useIsAdmin();
  const tgConfigured = useIntegrations().data?.telegram.configured;
  return (
    <>
      <p className="muted sm" style={{ marginTop: 0 }}>
        Уведомления о письмах, дайджест в выбранные дни, напоминания за час до шага; боту можно пересылать
        вакансии.
      </p>
      {!admin && tgConfigured === false && <p className="muted sm">Бот ещё не настроен администратором.</p>}
      {admin && <SecretsReadOnlyNote />}
      {admin && (
        <SecretField
          name="telegramBotToken"
          label="Токен бота"
          placeholder="1234567890:AAH…"
          info={<TelegramTokenGuide />}
          help={
            <>
              В Telegram напишите{' '}
              <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer">
                @BotFather
              </a>{' '}
              → <code>/newbot</code> → имя → username на <code>bot</code>. Он пришлёт токен — вставьте его
              сюда.
            </>
          }
        />
      )}
      {admin && (
        <details className="raw" style={{ marginTop: 14 }}>
          <summary>Чтение каналов для ленты вакансий (администратор)</summary>
          <SecretField
            name="telegramApiId"
            label="api_id приложения Telegram"
            secret={false}
            numeric
            placeholder="1234567"
            info={<TelegramAppGuide />}
          />
          <SecretField name="telegramApiHash" label="api_hash приложения Telegram" placeholder="32 символа" />
        </details>
      )}
      <TelegramLink />
      <div className="grid" style={{ marginTop: 10 }}>
        <TimeField
          label="Время дайджеста"
          name="digestTime"
          value={s.digestTime}
          onSave={(v) => set({ digestTime: v })}
        />
        <div className="f">
          Дни дайджеста
          <div className="days" role="group" aria-label="Дни дайджеста">
            {DAYS.map(([d, short, full]) => {
              const on = s.digestDays.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  aria-label={full}
                  title={full}
                  onClick={() => {
                    const next = on ? s.digestDays.filter((x) => x !== d) : [...s.digestDays, d];
                    set({ digestDays: next }).catch(() => ui.toast('Не сохранилось. Повторите.'));
                  }}
                >
                  {short}
                </button>
              );
            })}
          </div>
          <span className="muted sm">
            {s.digestDays.length === 0
              ? 'Дайджест не приходит'
              : s.digestDays.length === 7
                ? 'Каждый день'
                : s.digestDays.length === 5 && !s.digestDays.includes(0) && !s.digestDays.includes(6)
                  ? 'По будням'
                  : `${s.digestDays.length} ${s.digestDays.length === 1 ? 'день' : s.digestDays.length < 5 ? 'дня' : 'дней'} в неделю`}
          </span>
        </div>
        <div className="f full">
          Какие события присылать
          <div className="btnrow" style={{ marginTop: 4 }}>
            {NOTIFY_EVENTS.map((k) => (
              <label className="btn sm" key={k}>
                <input
                  type="checkbox"
                  checked={s.notifyEvents.includes(k)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...new Set([...s.notifyEvents, k])]
                      : s.notifyEvents.filter((x) => x !== k);
                    set({ notifyEvents: next as (typeof NOTIFY_EVENTS)[number][] }).catch(() =>
                      ui.toast('Не сохранилось. Повторите.'),
                    );
                  }}
                />{' '}
                {NOTIFY_LABELS[k]}
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/** Привязка чата: одноразовый код на 10 минут → /start <код> боту. */
function TelegramLink() {
  const ui = useUI();
  const qc = useQueryClient();
  const q = useIntegrations();
  const tg = q.data?.telegram;
  const [code, setCode] = useState<{ code: string; url: string | null; expiresAt: string } | null>(null);
  useEffect(() => {
    if (!code || tg?.linked) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: integrationsKey }), 3000);
    return () => clearInterval(t);
  }, [code, tg?.linked, qc]);
  useEffect(() => {
    if (code && tg?.linked) {
      setCode(null);
      ui.toast('Telegram привязан');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    }
  }, [code, tg?.linked, ui, qc]);
  if (!tg) return null;
  if (!tg.configured) return null;
  if (tg.linked)
    return (
      <div className="btnrow">
        <span className="muted sm" style={{ alignSelf: 'center' }}>
          Бот {tg.botUsername ? `@${tg.botUsername}` : ''} привязан: пересылайте ему вакансии, команды /today,
          /inbox, /stats.
        </span>
        <ConfirmButton
          className="btn sm ghost danger"
          confirmText="Отвязать?"
          onConfirm={async () => {
            await del('/integrations/telegram');
            await qc.invalidateQueries({ queryKey: integrationsKey });
            ui.toast('Telegram отвязан');
          }}
        >
          Отвязать
        </ConfirmButton>
      </div>
    );
  return code ? (
    <div className="pform" role="status">
      <p style={{ margin: '0 0 8px' }}>
        {code.url ? (
          <>
            Откройте{' '}
            <a href={code.url} target="_blank" rel="noopener noreferrer">
              бота в Telegram
            </a>{' '}
            и нажмите «Start» — или отправьте ему:
          </>
        ) : (
          'Отправьте боту сообщение:'
        )}
      </p>
      <pre style={{ margin: 0, fontSize: 16 }}>/start {code.code}</pre>
      <p className="muted sm" style={{ margin: '8px 0 0' }}>
        Код действует 10 минут. Жду привязки…
      </p>
    </div>
  ) : (
    <button
      className="btn primary"
      onClick={async () => {
        try {
          setCode(
            await post<{ code: string; url: string | null; expiresAt: string }>(
              '/integrations/telegram/link',
            ),
          );
        } catch (x) {
          ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
        }
      }}
    >
      <Icon name="send" /> Привязать Telegram
    </button>
  );
}

/** «Проверить сейчас» и время последней проверки. */
function ScanNow() {
  const ui = useUI();
  const qc = useQueryClient();
  const q = useIntegrations();
  const g = q.data?.gmail;
  const [busy, setBusy] = useState(false);
  if (!g) return null;
  const scan = async () => {
    setBusy(true);
    try {
      await post('/integrations/gmail/scan');
      ui.toast('Проверяю почту — новое появится в «Сообщениях»');
      setTimeout(() => void qc.invalidateQueries({ queryKey: integrationsKey }), 5000);
    } catch (x) {
      ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="btnrow">
      <button
        className="btn primary"
        type="button"
        disabled={busy || g.needsReauth}
        onClick={() => void scan()}
      >
        <Icon name="mail" /> {busy ? 'Ставлю в очередь…' : 'Проверить сейчас'}
      </button>
      <span className="muted sm" style={{ alignSelf: 'center' }}>
        {g.email ? `${g.email} · ` : ''}
        {g.lastScanAt ? `проверено ${when(g.lastScanAt)}` : 'ещё не проверялась'}
        {g.lastError ? ` · ошибка: ${g.lastError}` : ''}
      </span>
    </div>
  );
}

function ClaudeSettings({ s, set }: { s: Settings; set: SetFn }) {
  const admin = useIsAdmin();
  const c = useIntegrations().data?.claude;
  const n = (v: number) => v.toLocaleString('ru-RU');
  return (
    <>
      <p className="muted sm" style={{ marginTop: 0 }}>
        Разбор вставленных текстов и писем, черновики ответов, подготовка к интервью.
      </p>
      {admin && <SecretsReadOnlyNote />}
      {admin && (
        <SecretField
          name="anthropicApiKey"
          label="Ключ Anthropic API"
          placeholder="sk-ant-…"
          info={<AnthropicKeyGuide />}
          help={
            <>
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">
                console.anthropic.com
              </a>{' '}
              → API Keys → Create Key. Оплата — по факту использования в аккаунте Anthropic. Ключ общий для
              всех пользователей; лимит токенов каждому задаётся в «Пользователях».
            </>
          }
        />
      )}
      {c?.configured && (
        <div className="usage">
          <div>
            Расход трекера за этот месяц
            <b>{n(c.tokensThisMonth)}</b>
            <span className="muted sm">
              токенов
              {c.monthlyLimit
                ? ` из ${n(c.monthlyLimit)} (${Math.round((c.tokensThisMonth / c.monthlyLimit) * 100)} %)`
                : ''}
            </span>
          </div>
          {c.monthlyLimit ? (
            <span className="ubar" aria-hidden="true">
              <span style={{ width: `${Math.min(100, (c.tokensThisMonth / c.monthlyLimit) * 100)}%` }} />
            </span>
          ) : null}
          <span className="muted sm">
            {admin ? `Модель: ${c.model}. Считаются только ваши запросы.` : 'Лимит задаёт администратор.'}
          </span>
        </div>
      )}
      {admin && (
        <div className="grid" style={{ marginTop: 14 }}>
          <TextField
            label="Мой месячный лимит токенов (пусто — без лимита)"
            name="aiMonthlyTokenLimit"
            type="number"
            nullable
            value={s.aiMonthlyTokenLimit}
            onSave={(v) => set({ aiMonthlyTokenLimit: v ? Number(v) : null })}
          />
        </div>
      )}
    </>
  );
}

/** Состояние почты для цветной точки. */
export function mailHealth(g: IntegrationsStatus['gmail'] | undefined): Health {
  if (!g) return 'off';
  if (g.needsReauth || (g.connected && g.lastError)) return 'bad';
  return g.connected ? 'ok' : 'off';
}
